/**
 * From a response data object, return the best user-facing error string.
 * Priority: (a) first from errors[] (b) first from data.errors[] (c) message (d) error (e) null.
 * Handles errors as string[] or object[] with .error / .message / .msg; objects with rowIndex become "Row X: msg".
 * @param {any} data - response.data or similar
 * @returns {string|null}
 */
export function getBestErrorMessageFromData(data) {
  if (!data) return null;

  const firstFrom = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    const first = arr[0];
    if (typeof first === "string" && first.trim()) return first.trim();
    const msg = first?.error ?? first?.message ?? first?.msg;
    const str = msg != null ? (typeof msg === "string" ? msg : String(msg)) : null;
    if (!str) return null;
    const row = first?.rowIndex ?? first?.row;
    if (row != null) return `Row ${row}: ${str}`;
    return str;
  };

  let out = firstFrom(data.errors);
  if (out) return out;
  out = firstFrom(data.data?.errors);
  if (out) return out;
  if (data.message && typeof data.message === "string" && data.message.trim()) return data.message.trim();
  if (data.error && typeof data.error === "string" && data.error.trim()) return data.error.trim();
  return null;
}

/**
 * Extract a user-facing error message from API error or response.
 * Prefers detailed validation errors (errors[] / data.errors[]) over generic message.
 * Handles backend shapes: { error }, { message }, { success: false, errors: [...] }, express-validator/zod arrays.
 * @param {Error|{ response?: { data?: any }, message?: string }|any} err
 * @returns {string}
 */
export function getApiErrorMessage(err) {
  const data = err?.response?.data;

  const best = getBestErrorMessageFromData(data);
  if (best) return best;

  // backend may send plain string
  if (typeof data === "string" && data.trim()) return data.trim();

  // sometimes nested
  if (data?.data?.error) return typeof data.data.error === "string" ? data.data.error : String(data.data.error);
  if (data?.data?.message) return typeof data.data.message === "string" ? data.data.message : String(data.data.message);

  // axios / fetch network
  if (err?.message) return err.message;

  return "Operation failed. Please try again.";
}

/**
 * Extract error message for bulk upload responses.
 * Prefers first detailed error from errors[] or data.errors[], then message, then generic.
 * Backend may send: { errors: [{ rowIndex, error }] }, { data: { errors } }, { error }, { message }.
 * @param {Error|{ response?: { data?: any }, data?: any }|any} err
 * @returns {string}
 */
export function getUploadErrorMsg(err) {
  const data = err?.response?.data ?? err?.data ?? err;

  const best = getBestErrorMessageFromData(data);
  if (best) return best;

  if (typeof data === "string" && data.trim()) return data.trim();

  return "Upload failed. Fix the errors and re-upload. No records were created.";
}
